import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';

type AuthMode = 'login' | 'register' | 'guest' | 'age' | 'permission';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>('login');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    const m = searchParams.get('mode');
    if (m === 'register') setMode('register');
    else if (m === 'guest') setMode('age');
    else setMode('login');
  }, [searchParams]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!ageConfirmed) { setError('18+ yaş onayı gerekli.'); return; }
    if (!form.email || !form.password) { setError('Tüm alanları doldurun.'); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setMode('permission'); }, 1200);
  };

  const handleGuestStart = () => {
    if (!ageConfirmed) { setError('18+ yaş onayını vermeniz gerekiyor.'); return; }
    setMode('permission');
  };

  const handlePermission = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); navigate('/matching'); }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#05070d] flex flex-col">
      <Header />
      {/* BG orbs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] rounded-full bg-violet-600/8 blur-[100px]" />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-20">
        {/* Age Confirmation Screen */}
        {mode === 'age' && (
          <div className="w-full max-w-md">
            <div className="bg-[#0d1117]/80 backdrop-blur-xl rounded-3xl border border-white/10 p-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-orange-500/15 border border-orange-500/30 mx-auto mb-6">
                <i className="ri-shield-user-line text-orange-400 text-4xl" />
              </div>
              <h2 className="text-white font-extrabold text-2xl mb-3">Yaş Doğrulama</h2>
              <p className="text-white/50 text-sm leading-relaxed mb-8">
                ChatNow yalnızca <strong className="text-white">18 yaş ve üzeri</strong> kullanıcılar içindir. Devam etmek için yaşınızı onaylayın.
              </p>
              <label className="flex items-start gap-3 mb-8 cursor-pointer group text-left">
                <div
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${ageConfirmed ? 'bg-cyan-500 border-cyan-500' : 'border-white/20 group-hover:border-white/40'}`}
                  onClick={() => setAgeConfirmed(!ageConfirmed)}
                >
                  {ageConfirmed && <i className="ri-check-line text-white text-xs" />}
                </div>
                <span className="text-white/60 text-sm leading-relaxed">
                  18 yaşından büyük olduğumu ve{' '}
                  <a href="#" className="text-cyan-400 hover:underline">kullanım şartlarını</a>{' '}
                  kabul ettiğimi onaylıyorum.
                </span>
              </label>
              {error && <p className="text-red-400 text-xs mb-4">{error}</p>}
              <button
                onClick={handleGuestStart}
                className="w-full py-4 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-base whitespace-nowrap cursor-pointer hover:from-cyan-400 hover:to-violet-500 transition-all"
              >
                Devam Et — Misafir Olarak Başla
              </button>
              <p className="text-white/30 text-xs mt-4">
                Üye misin?{' '}
                <button className="text-cyan-400 hover:underline cursor-pointer" onClick={() => setMode('login')}>
                  Giriş Yap
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Camera/Mic Permission Screen */}
        {mode === 'permission' && (
          <div className="w-full max-w-md">
            <div className="bg-[#0d1117]/80 backdrop-blur-xl rounded-3xl border border-white/10 p-10 text-center">
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-cyan-500/15 border border-cyan-500/30">
                  <i className="ri-camera-line text-cyan-400 text-3xl" />
                </div>
                <div className="w-8 h-px bg-white/20" />
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-violet-500/15 border border-violet-500/30">
                  <i className="ri-mic-line text-violet-400 text-3xl" />
                </div>
              </div>
              <h2 className="text-white font-extrabold text-2xl mb-3">İzin Gerekli</h2>
              <p className="text-white/50 text-sm leading-relaxed mb-8">
                Görüntülü sohbet için kamera ve mikrofon izninize ihtiyacımız var. Tarayıcınız izin isteyecek — <strong className="text-white">"İzin Ver"</strong> seçeneğini seçin.
              </p>
              <div className="flex flex-col gap-3 mb-6 text-left">
                {[
                  { icon: 'ri-camera-line', text: 'Kamera — Görüntünüzü iletmek için', color: 'text-cyan-400' },
                  { icon: 'ri-mic-line', text: 'Mikrofon — Sesinizi iletmek için', color: 'text-violet-400' },
                  { icon: 'ri-lock-line', text: 'Veriler şifreli, kayıt edilmez', color: 'text-emerald-400' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/3 border border-white/8">
                    <i className={`${item.icon} ${item.color}`} />
                    <span className="text-white/60 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={handlePermission}
                disabled={loading}
                className="w-full py-4 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-base cursor-pointer hover:from-cyan-400 hover:to-violet-500 transition-all disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Hazırlanıyor...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-video-on-line" /> Eşleşmeye Başla
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Login / Register */}
        {(mode === 'login' || mode === 'register') && (
          <div className="w-full max-w-md">
            <div className="bg-[#0d1117]/80 backdrop-blur-xl rounded-3xl border border-white/10 p-10">
              {/* Logo */}
              <div className="text-center mb-8">
                <img
                  src="https://public.readdy.ai/ai/img_res/0758b00e-4705-432c-90d8-8173dccd9e57.png"
                  alt="ChatNow"
                  className="h-10 w-auto object-contain mx-auto mb-6"
                />
                <h2 className="text-white font-extrabold text-2xl mb-1">
                  {mode === 'login' ? 'Tekrar Hoş Geldin' : 'Hesap Oluştur'}
                </h2>
                <p className="text-white/45 text-sm">
                  {mode === 'login' ? 'Hesabına giriş yap' : 'Ücretsiz hesabını oluştur'}
                </p>
              </div>

              {/* Tab switcher */}
              <div className="flex bg-white/5 rounded-full p-1 mb-8">
                <button
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'login' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                >
                  Giriş Yap
                </button>
                <button
                  onClick={() => setMode('register')}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'register' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                >
                  Kayıt Ol
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {mode === 'register' && (
                  <div>
                    <label className="block text-white/50 text-xs font-semibold mb-2 uppercase tracking-wide">Kullanıcı Adı</label>
                    <div className="relative">
                      <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                      <input
                        type="text"
                        placeholder="kullaniciadi"
                        value={form.username}
                        onChange={(e) => setForm({ ...form, username: e.target.value })}
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-500/50 transition-colors"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-white/50 text-xs font-semibold mb-2 uppercase tracking-wide">E-posta</label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                    <input
                      type="email"
                      placeholder="ornek@email.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/50 text-xs font-semibold mb-2 uppercase tracking-wide">Şifre</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full pl-10 pr-12 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 cursor-pointer"
                    >
                      <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>

                {/* Age confirm */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${ageConfirmed ? 'bg-cyan-500 border-cyan-500' : 'border-white/20 group-hover:border-white/40'}`}
                    onClick={() => setAgeConfirmed(!ageConfirmed)}
                  >
                    {ageConfirmed && <i className="ri-check-line text-white text-xs" />}
                  </div>
                  <span className="text-white/45 text-xs leading-relaxed">
                    18 yaşından büyük olduğumu, <a href="#" className="text-cyan-400 hover:underline">kullanım şartlarını</a> ve <a href="#" className="text-cyan-400 hover:underline">gizlilik politikasını</a> okuduğumu onaylıyorum.
                  </span>
                </label>

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <i className="ri-error-warning-line" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-base cursor-pointer hover:from-cyan-400 hover:to-violet-500 transition-all disabled:opacity-60 mt-2"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" /> İşleniyor...
                    </span>
                  ) : mode === 'login' ? 'Giriş Yap' : 'Kayıt Ol'}
                </button>
              </form>

              <div className="relative flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-white/8" />
                <span className="text-white/25 text-xs">veya</span>
                <div className="flex-1 h-px bg-white/8" />
              </div>

              <Link
                to="/auth?mode=guest"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm font-medium hover:bg-white/8 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-ghost-line" />
                Misafir Olarak Başla
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
